import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { getStoryAudio, getStoryHistory } from "../api/stories";
import type { AudioUrlResponse, StoryHistoryResponse } from "../types";
import { CompactAudioPlayer } from "./CompactAudioPlayer";

const ACCENT_COLORS = ["#4A7DE8", "#E84B6A", "#3ED8C3", "#A855F7", "#F97316"];

interface Props {
	storyId: string;
	onClose: () => void;
}

export function SourceMaterialModal({ storyId, onClose }: Props) {
	const { t } = useTranslation();
	const overlayRef = useRef<HTMLDivElement>(null);

	const { data: history, isLoading: historyLoading } =
		useQuery<StoryHistoryResponse>({
			queryKey: ["story-history", storyId],
			queryFn: () => getStoryHistory(storyId),
		});

	const { data: audioData } = useQuery<AudioUrlResponse>({
		queryKey: ["story-audio", storyId],
		queryFn: () => getStoryAudio(storyId),
	});

	// Close on Escape
	useEffect(() => {
		function onKey(e: KeyboardEvent) {
			if (e.key === "Escape") onClose();
		}
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [onClose]);

	const hasAudio = !!audioData?.url;
	const hasTranscript = !!history?.original_transcript;

	return (
		<div
			ref={overlayRef}
			className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 backdrop-blur-sm"
		>
			<button
				type="button"
				onClick={onClose}
				className="absolute inset-0"
				aria-label={t("stories.detail.sourceMaterial.close")}
			/>
			<div className="relative w-full max-w-2xl max-h-[85vh] bg-[#F5F0E8] rounded-t-sm overflow-hidden flex flex-col">
				{/* Grain texture overlay */}
				<div
					className="absolute inset-0 pointer-events-none opacity-[0.04]"
					style={{
						backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
						backgroundSize: "128px",
					}}
				/>

				{/* Header */}
				<div className="flex items-center justify-between px-6 py-5 border-b border-ink/10">
					<span className="font-ui text-[10px] uppercase tracking-[0.25em] text-ink/50">
						{t("stories.detail.sourceMaterial.trigger")}
					</span>
					<button
						type="button"
						onClick={onClose}
						className="font-ui text-[10px] uppercase tracking-widest text-ink/40 hover:text-ink/70 transition-colors"
					>
						{t("stories.detail.sourceMaterial.close")}
					</button>
				</div>

				{/* Scrollable content */}
				<div className="overflow-y-auto flex-1 px-6 py-8 flex flex-col gap-10">
					{historyLoading && (
						<div className="flex gap-2 items-center py-4">
							{[0, 1, 2].map((i) => (
								<span
									key={i}
									className="w-1.5 h-1.5 rounded-full bg-ink/30"
									style={{
										animation: `pulse 1.4s ease-in-out ${i * 0.2}s infinite`,
									}}
								/>
							))}
						</div>
					)}

					{/* Initial story */}
					<section className="flex flex-col gap-5">
						<p className="font-ui text-[10px] uppercase tracking-[0.25em] text-ink/40">
							{t("stories.detail.sourceMaterial.initialStory")}
						</p>
						{hasAudio && (
							<CompactAudioPlayer
								url={audioData.url ?? ""}
								accent={ACCENT_COLORS[0] ?? "#4A7DE8"}
							/>
						)}
						{hasTranscript && (
							<p className="font-body italic text-ink/70 text-base leading-relaxed whitespace-pre-wrap">
								{history?.original_transcript}
							</p>
						)}
					</section>

					{/* Q&A rounds */}
					{history?.qa_sections.map((section, sectionIdx) => {
						const answeredPairs = section.qa_pairs.filter(
							(p) => p.answer !== null || p.audio_url !== null,
						);
						const sectionKey = answeredPairs
							.map((pair) => pair.question)
							.join("|");
						if (answeredPairs.length === 0) return null;
						return (
							<section key={sectionKey || `section-${storyId}`}>
								<WavyDivider />
								<p className="font-ui text-[10px] uppercase tracking-[0.25em] text-ink/40 mb-6 mt-10">
									{t("stories.detail.sourceMaterial.qaRound", {
										number: sectionIdx + 1,
									})}
								</p>
								<div className="flex flex-col gap-6">
									{answeredPairs.map((pair, pairIdx) => {
										const accent =
											ACCENT_COLORS[pairIdx % ACCENT_COLORS.length] ??
											"#4A7DE8";
										return (
											<div
												key={`${pair.question}-${pair.answer ?? pair.audio_url ?? "unanswered"}`}
												className="pl-4"
												style={{ borderLeft: `2px solid ${accent}` }}
											>
												<p className="font-body text-ink/60 text-sm leading-snug mb-2">
													{pair.question}
												</p>
												{pair.audio_url && (
													<div className="mb-2">
														<CompactAudioPlayer
															url={pair.audio_url}
															accent={accent}
														/>
													</div>
												)}
												<p className="font-body italic text-ink/80 text-base leading-relaxed">
													{pair.answer ??
														t("stories.detail.sourceMaterial.skipped")}
												</p>
											</div>
										);
									})}
								</div>
							</section>
						);
					})}
				</div>
			</div>
		</div>
	);
}

function WavyDivider() {
	return (
		<svg
			viewBox="0 0 300 12"
			className="w-full"
			preserveAspectRatio="none"
			aria-hidden="true"
		>
			<title>Decorative section divider</title>
			<path
				d="M0 6 Q15 1 30 6 Q45 11 60 6 Q75 1 90 6 Q105 11 120 6 Q135 1 150 6 Q165 11 180 6 Q195 1 210 6 Q225 11 240 6 Q255 1 270 6 Q285 11 300 6"
				stroke="#1a120920"
				strokeWidth="1"
				fill="none"
			/>
		</svg>
	);
}
